import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { fetchBlogPosts } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import CtaBand from "../components/CtaBand";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title={t("blog.list_seo_title")}
        description={t("blog.list_seo_description")}
        keywords={t("blog.list_seo_keywords")}
      />

      <section className="bg-[#082c50] text-white py-16" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs tracking-[0.2em] uppercase text-[#1a5193] font-chivo font-bold">
            {t("blog.eyebrow")}
          </span>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3">
            {t("blog.h1")}
          </h1>
          <p className="mt-4 text-gray-300 max-w-2xl">{t("blog.sub")}</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-96 bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="blog-grid">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col"
                >
                  <Link to={`/blog/${p.slug}`} className="block">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="mt-5">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {p.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] tracking-[0.2em] uppercase font-chivo font-bold text-[#1a5193] border border-[#1a5193]/30 px-2 py-1"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="font-chivo font-bold text-xl text-[#082c50] leading-tight group-hover:text-[#1a5193] transition-colors">
                        {p.title}
                      </h2>
                      <p className="text-sm text-gray-600 mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <div className="mt-4 inline-flex items-center text-xs font-chivo font-bold tracking-wide uppercase text-[#1a5193]">
                        {t("blog.read_article")} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
